import { asNullableSiValue, dpForSpec, type FieldUnitSpec, resolveIn, type SiValue } from "./measure";
import { findUnit } from "./registry";

export type UnitFormatOptions = { locale?: string; placeholder?: string; separator?: string };

export type MeasurePartsOptions = { dp?: number | Readonly<Record<string, number>> };

export type MeasureFormatOptions = UnitFormatOptions & MeasurePartsOptions;

export type MeasureParts = { value: number | null; dp: number; symbol: string };

export const formatNumberWithUnit = (
  value: number | null | undefined,
  symbol: string,
  dp: number,
  { locale, placeholder = "-", separator = " " }: UnitFormatOptions = {},
): string => {
  // A NaN or Infinity from a degenerate formula would otherwise ship as "NaN kPa" in a client report.
  if (value == null || !Number.isFinite(value)) return placeholder;
  const normalized = Object.is(value, -0) ? 0 : value;
  const formatted = normalized.toLocaleString(locale, { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return `${formatted}${separator}${symbol}`;
};

// The value and its symbol, resolved but not yet formatted — for callers that render them as separate
// nodes (a unit the user can click) or apply their own formatting (report templates carry their own dp).
export const measureParts = (
  value: SiValue | number | null | undefined,
  spec: FieldUnitSpec,
  unitId: string = spec.canonical,
  { dp }: MeasurePartsOptions = {},
): MeasureParts => {
  // Resolved before the null check so an unknown unit id fails on every call, not only on populated fields.
  const unit = findUnit(spec.quantity, unitId);
  const resolved = asNullableSiValue(value, spec);
  // `si` is typed number, but it arrives from persisted JSON hydrated with `?? null`; unguarded,
  // resolveIn's arithmetic turns a null into a converted 0 while the canonical unit shows "-".
  const converted = resolved?.si == null ? null : resolveIn(resolved, spec, unit.id);
  const finite = converted != null && Number.isFinite(converted);
  return {
    // An exact -0 survives conversion and would print as "-0.0" from a caller applying its own dp.
    // A near-zero negative still rounds to "-0.0" — that needs normalizing after rounding, which is
    // the caller's step, not this one (`roundToDisplay` in the app is the pattern).
    value: finite ? (Object.is(converted, -0) ? 0 : converted) : null,
    dp: typeof dp === "number" ? dp : (dp?.[unit.id] ?? dpForSpec(spec, unit.id)),
    symbol: unit.symbol,
  };
};

export const formatMeasure = (
  value: SiValue | number | null | undefined,
  spec: FieldUnitSpec,
  unitId: string = spec.canonical,
  { dp, ...options }: MeasureFormatOptions = {},
): string => {
  const parts = measureParts(value, spec, unitId, { dp });
  return formatNumberWithUnit(parts.value, parts.symbol, parts.dp, options);
};

export const toleranceParts = (
  canonicalDelta: number,
  spec: FieldUnitSpec,
  unitId: string = spec.canonical,
  { dp }: MeasurePartsOptions = {},
): MeasureParts => {
  const unit = findUnit(spec.quantity, unitId);
  const canonical = findUnit(spec.quantity, spec.canonical);
  return {
    value: (canonicalDelta * canonical.factor) / unit.factor,
    dp: typeof dp === "number" ? dp : (dp?.[unit.id] ?? dpForSpec(spec, unit.id)),
    symbol: unit.symbol,
  };
};

export const formatTolerance = (
  canonicalDelta: number,
  spec: FieldUnitSpec,
  unitId: string = spec.canonical,
  { dp, ...options }: MeasureFormatOptions = {},
): string => {
  const parts = toleranceParts(canonicalDelta, spec, unitId, { dp });
  return formatNumberWithUnit(parts.value, parts.symbol, parts.dp, options);
};
